import config from '../config';
import selectedEnvironment from '../env';
const API_URL = `${config[selectedEnvironment].API_URL}/TaxAuthority`;
const requestTokenClient = async (caseId, auth, scope = null) => {
    try {
        const body = { CaseId: caseId };
        if (scope) {
            body.Scope = scope;
        }
        
        const response = await fetch(`${API_URL}/tokenRequest`, {
            method: 'POST',
            headers: {
                "Authorization": 'Bearer ' + auth,
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(body),
        });

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        return await response.json();
    } catch (error) {
        console.error('Error fetching token:', error);
        throw error;
    }
};

const checkAuthCompletionClient = async (caseId, scope = null) => {
    try {
        const url = scope 
            ? `${API_URL}/checkAuthCompletion?caseId=${caseId}&scope=${scope}`
            : `${API_URL}/checkAuthCompletion?caseId=${caseId}`;
            
        const response = await fetch(url);
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        return await response.json();
    } catch (error) {
        console.error('Error checking authentication completion:', error);
        throw error;
    }
};

const requestTokenAccountant = async (accountantId,scope,auth) => {
    try {
        const response = await fetch(`${API_URL}/tokenRequestAccountant`, {
            method: 'POST',
            headers: {
                "Authorization": 'Bearer ' + auth,
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ accountantId,scope }),
        });

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        return await response.json();
    } catch (error) {
        console.error('Error fetching token:', error);
        throw error;
    }
};

const checkAuthCompletionAccountant = async (accountantId,scope,auth) => {
    try {
        const response = await fetch(`${API_URL}/checkAccountantAuthCompletion?accountantId=${accountantId}&scope=${scope}`,{
            headers: {
                "Authorization": 'Bearer ' + auth,
                'Content-Type': 'application/json',
            },
        });
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        return await response.json();
    } catch (error) {
        console.error('Error checking authentication completion:', error);
        throw error;
    }
};

const getTokenDaysRemaining = async (caseId, auth, scope = null) => {
    try {
        const url = scope 
            ? `${API_URL}/tokenDaysRemaining/${caseId}?scope=${scope}`
            : `${API_URL}/tokenDaysRemaining/${caseId}`;
        
        const response = await fetch(url, {
            method: 'GET',
            headers: {
                "Authorization": 'Bearer ' + auth,
                'Content-Type': 'application/json',
            },
        });

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        return await response.json(); // Assuming the response contains `{ daysRemaining: number }`
    } catch (error) {
        console.error('Error fetching token days remaining:', error);
        throw error;
    }
};

const deleteClientToken = async (caseId, auth) => {
    try {
        const response = await fetch(`${API_URL}/token/${caseId}`, {
            method: 'DELETE',
            headers: {
                "Authorization": 'Bearer ' + auth,
                'Content-Type': 'application/json',
            },
        });

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        return await response.json();
    } catch (error) {
        console.error('Error deleting client token:', error);
        throw error;
    }
};

// ─── Invoice Decision API Methods ─────────────────────────────────────────────

const processInvoiceDecision = async (requestData, auth) => {
    try {
        console.log('Processing invoice decision with data:', auth);
        
        const response = await fetch(`${API_URL}/invoiceDecision/process`, {
            method: 'POST',
            headers: {
                "Authorization": 'Bearer ' + auth,
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(requestData),
        });

        console.log('Response status:', response.status);
        console.log('Response headers:', response.headers);

        // Check if response has content
        const responseText = await response.text();
        console.log('Response text:', responseText);

        let responseData;
        if (responseText && responseText.trim()) {
            try {
                responseData = JSON.parse(responseText);
                console.log('Parsed response data:', responseData);
            } catch (parseError) {
                console.error('Failed to parse JSON response:', parseError);
                throw new Error(`Invalid JSON response: ${responseText}`);
            }
        } else {
            console.log('Empty response received, creating default success response');
            responseData = {
                success: true,
                message: 'Operation completed successfully',
                status: 200,
                timestamp: new Date().toISOString()
            };
        }

        if (!response.ok) {
            throw new Error(responseData.message || responseData.error || `HTTP error! status: ${response.status}`);
        }

        return responseData;
    } catch (error) {
        console.error('Error processing invoice decision:', error);
        throw error;
    }
};

const checkInvoiceDecisionHealth = async (caseId, auth) => {
    try {
        const response = await fetch(`${API_URL}/invoiceDecision/health?caseId=${caseId}`, {
            method: 'GET',
            headers: {
                "Authorization": 'Bearer ' + auth,
                'Content-Type': 'application/json',
            },
        });

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        return await response.json();
    } catch (error) {
        console.error('Error checking invoice decision health:', error);
        throw error;
    }
};

const checkInvoiceDecisionAlive = async (caseId, auth) => {
    try {
        const response = await fetch(`${API_URL}/invoiceDecision/alive?caseId=${caseId}`, {
            method: 'GET',
            headers: {
                "Authorization": 'Bearer ' + auth,
                'Content-Type': 'application/json',
            },
        });

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        return await response.json();
    } catch (error) {
        console.error('Error checking invoice decision alive status:', error);
        throw error;
    }
};

// ─── Accountant Token Management Methods ─────────────────────────────────────────────

const getAccountantTokenDaysRemaining = async (accountantId, scope, auth) => {
    try {
        const response = await fetch(`${API_URL}/accountantTokenDaysRemaining/${accountantId}?scope=${scope}`, {
            method: 'GET',
            headers: {
                "Authorization": 'Bearer ' + auth,
                'Content-Type': 'application/json',
            },
        });

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        return await response.json();
    } catch (error) {
        console.error('Error fetching accountant token days remaining:', error);
        throw error;
    }
};

const deleteAccountantToken = async (accountantId, scope, auth) => {
    try {
        const response = await fetch(`${API_URL}/accountantToken/${accountantId}?scope=${scope}`, {
            method: 'DELETE',
            headers: {
                "Authorization": 'Bearer ' + auth,
                'Content-Type': 'application/json',
            },
        });

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        return await response.json();
    } catch (error) {
        console.error('Error deleting accountant token:', error);
        throw error;
    }
};

// ─── Pending decision guard ───────────────────────────────────────────────────

const getPendingInvoiceDecisionStatus = async (caseId, auth, documentType = 'TaxInvoice', invoiceId = null) => {
    try {
        const params = new URLSearchParams({ caseId, documentType });
        if (invoiceId) params.append('invoiceId', invoiceId);
        const response = await fetch(`${API_URL}/invoiceDecision/pendingStatus?${params.toString()}`, {
            method: 'GET',
            headers: {
                Authorization: 'Bearer ' + auth,
                'Content-Type': 'application/json',
            },
        });
        if (!response.ok) {
            const text = await response.text();
            throw new Error(text || `HTTP error! status: ${response.status}`);
        }
        return await response.json();
    } catch (error) {
        console.error('Error fetching pending invoice decision status:', error);
        throw error;
    }
};

const resolvePendingInvoiceDecision = async (requestData, auth) => {
    try {
        const response = await fetch(`${API_URL}/invoiceDecision/resolvePending`, {
            method: 'POST',
            headers: {
                Authorization: 'Bearer ' + auth,
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(requestData),
        });
        const responseText = await response.text();
        let responseData = {};
        if (responseText && responseText.trim()) {
            try {
                responseData = JSON.parse(responseText);
            } catch {
                throw new Error(responseText);
            }
        }
        if (!response.ok) {
            throw new Error(responseData.message || responseData.error || `HTTP error! status: ${response.status}`);
        }
        return responseData;
    } catch (error) {
        console.error('Error resolving pending invoice decision:', error);
        throw error;
    }
};

const getProblemInvoiceDecisions = async (caseId, auth) => {
    try {
        const params = new URLSearchParams({ caseId });
        const response = await fetch(`${API_URL}/invoiceDecision/problemInvoices?${params.toString()}`, {
            method: 'GET',
            headers: {
                Authorization: 'Bearer ' + auth,
                'Content-Type': 'application/json',
            },
        });
        if (!response.ok) {
            const text = await response.text();
            throw new Error(text || `HTTP error! status: ${response.status}`);
        }
        return await response.json();
    } catch (error) {
        console.error('Error fetching problem invoice decisions:', error);
        throw error;
    }
};

// ─── Supplier invoices (MultiInvoiceInformationApi) ───────────────────────────

/**
 * Reads the error body of a supplier-invoice call. The Tax Authority's 400 responses explain how to
 * fix the request (for example asking for a narrower date range), so that text is what the user
 * needs to see rather than the status code.
 */
const readSupplierInvoiceError = async (response) => {
    const text = await response.text();
    if (!text || !text.trim()) {
        return `HTTP error! status: ${response.status}`;
    }
    try {
        const parsed = JSON.parse(text);
        return parsed.message || parsed.error || text;
    } catch {
        return text;
    }
};

/**
 * Invoices the client's suppliers reported to the Tax Authority for a period, each already matched
 * against the expenses on file. Read-only — nothing is written until importSupplierInvoices.
 *
 * @param {{ caseId: string, startDate: string, endDate: string,
 *           supplierVatNumber?: string, dateField?: number, includeItems?: boolean }} params
 */
const compareSupplierInvoices = async (params, auth) => {
    const response = await fetch(`${API_URL}/supplierInvoices/compare`, {
        method: 'POST',
        headers: {
            Authorization: 'Bearer ' + auth,
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({
            CaseId: params.caseId,
            StartDate: params.startDate,
            EndDate: params.endDate,
            SupplierVatNumber: params.supplierVatNumber || null,
            DateField: params.dateField ?? 1,
            IncludeItems: !!params.includeItems,
        }),
    });

    if (!response.ok) {
        throw new Error(await readSupplierInvoiceError(response));
    }

    return await response.json();
};

/**
 * Creates expenses for the chosen supplier invoices and stamps the Tax Authority details onto the
 * ones already on file. Each entry carries its own action, so a single call can do both.
 *
 * @param {{ caseId: string, accountantId?: string, invoices: Array<object> }} params
 */
const importSupplierInvoices = async (params, auth) => {
    const response = await fetch(`${API_URL}/supplierInvoices/import`, {
        method: 'POST',
        headers: {
            Authorization: 'Bearer ' + auth,
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({
            CaseId: params.caseId,
            AccountantId: params.accountantId || null,
            Invoices: params.invoices,
        }),
    });

    if (!response.ok) {
        throw new Error(await readSupplierInvoiceError(response));
    }

    return await response.json();
};

/** Map TaxAuthorityErrorModal option value → resolvePending Action string */
const mapOptionToResolveAction = (option) => {
    switch (option) {
        case 'not_making':
            return 'Cancel';
        case 'continue_no_confirmation':
            return 'Continue';
        case 'mandatory_reversal':
            return 'FlipObligation';
        case 'request_for_use':
            return 'Obligation';
        default:
            return option;
    }
};

/** Map option / action → Decision flag for subsequent create (when needed) */
const mapOptionToDecisionFlag = (option) => {
    switch (option) {
        case 'not_making':
        case 'Cancel':
            return 0;
        case 'continue_no_confirmation':
        case 'Continue':
            return 1;
        case 'mandatory_reversal':
        case 'FlipObligation':
            return 2;
        case 'request_for_use':
        case 'Obligation':
        // Decisions are persisted under the InvoiceDecisionAction name, so a stored decision
        // being replayed arrives as FurtherObjection rather than the UI's Obligation.
        case 'FurtherObjection':
            return 3;
        default:
            return null;
    }
};

const attemptInvoicePayload = (attempt) =>
    attempt?.invoicePayload || attempt?.InvoicePayload || null;

const historyHasInvoicePayload = (history) =>
    Array.isArray(history) && history.some((a) => !!attemptInvoicePayload(a));

/** First confirmation attempt that carries a stored invoice line-item snapshot. */
const extractInvoicePayloadFromStatus = (pendingStatus) => {
    if (!pendingStatus) return null;
    const history = pendingStatus.confirmationHistory || pendingStatus.ConfirmationHistory || [];
    const list = Array.isArray(history) ? history : [];
    const failed = list
        .filter((a) => !(a.isSuccess ?? a.IsSuccess))
        .sort(
            (a, b) =>
                new Date(a.creationDate || a.CreationDate || 0) -
                new Date(b.creationDate || b.CreationDate || 0)
        );
    const candidates = [
        ...failed,
        pendingStatus.firstAttempt || pendingStatus.FirstAttempt,
        ...list,
    ];
    for (const attempt of candidates) {
        const payload = attemptInvoicePayload(attempt);
        if (payload) return payload;
    }
    return pendingStatus.invoicePayload || pendingStatus.InvoicePayload || null;
};

/**
 * Fresh pendingStatus can arrive with an empty confirmationHistory. An empty array is truthy,
 * so `status.history || row.history` used to drop the problem-invoice row's stored items.
 * Prefer the list that actually carries the snapshot SHAAM accepted.
 */
const mergePendingStatusWithHeldRow = (status, row) => {
    const invId =
        status?.invoiceId ||
        status?.InvoiceId ||
        row?.invoiceId ||
        row?.InvoiceId;
    const pickHistory = (...lists) => {
        const withPayload = lists.find(historyHasInvoicePayload);
        if (withPayload) return withPayload;
        return lists.find((list) => Array.isArray(list) && list.length > 0) || [];
    };
    const confirmationHistory = pickHistory(
        status?.confirmationHistory,
        status?.ConfirmationHistory,
        row?.confirmationHistory,
        row?.ConfirmationHistory
    );
    const documentType =
        status?.documentType ||
        status?.DocumentType ||
        row?.documentType ||
        row?.DocumentType;
    const source = status?.source || status?.Source || row?.source || row?.Source;
    const rowStatus = status?.status || status?.Status || row?.status || row?.Status;
    const accepted =
        status?.acceptedDecisionAction ||
        status?.AcceptedDecisionAction ||
        row?.acceptedDecisionAction ||
        row?.AcceptedDecisionAction;
    return {
        ...(row || {}),
        ...(status || {}),
        invoiceId: invId,
        InvoiceId: invId,
        documentType,
        DocumentType: documentType,
        source,
        Source: source,
        status: rowStatus,
        Status: rowStatus,
        acceptedDecisionAction: accepted,
        AcceptedDecisionAction: accepted,
        confirmationHistory,
        ConfirmationHistory: confirmationHistory,
        firstAttempt:
            status?.firstAttempt ||
            status?.FirstAttempt ||
            row?.firstAttempt ||
            row?.FirstAttempt,
    };
};

/**
 * Build a GenerateIncomePdf body from the locked Tax Authority snapshot + Decision.
 * After resolvePending (or for idempotent re-create), call this so the system still
 * saves a real PDF/document (zero cancel for Decision=0, real amounts for others).
 */
const buildGenerateBodyFromSnapshot = (invoicePayload, invoiceId, decisionFlag) => {
    if (!invoicePayload) return null;
    let base = invoicePayload;
    if (typeof invoicePayload === 'string') {
        try {
            base = JSON.parse(invoicePayload);
        } catch {
            return null;
        }
    }
    if (typeof base !== 'object' || base === null || Array.isArray(base)) return null;

    const body = { ...base };
    const lockedRef = String(invoiceId || body.Reference || body.reference || '').trim();
    if (lockedRef) {
        body.Reference = lockedRef;
        body.reference = lockedRef;
    }
    // Decision 0 is valid (Cancel → zero canceled invoice)
    if (decisionFlag !== null && decisionFlag !== undefined) {
        body.Decision = decisionFlag;
        body.decision = decisionFlag;
    }
    // Snapshot redacts signature for storage size / privacy
    if (body.Signature === '[redacted]' || body.signature === '[redacted]') {
        body.Signature = '';
        body.signature = '';
    }
    return body;
};

/**
 * Endpoint that owns each Tax Authority reference series. A held document has to be finished on
 * the endpoint that allocated its number: posting an invoice-receipt snapshot to
 * Incomes/GenerateIncomePdf issues a *tax invoice* off the tax-invoice counter (where the held
 * number reads as "last used", so it advances by one) and abandons the held invoice-receipt.
 */
const FINISH_DOCUMENT_ENDPOINTS = {
    TaxInvoice: 'Incomes/GenerateIncomePdf',
    CreditNote: 'Incomes/GenerateRefundIncomePdf',
    ReceiptTax: 'PaymentAcceptances/GeneratePaymentAcceptanceDocumentPdf',
    Receipt: 'PaymentAcceptances/GeneratePaymentAcceptanceDocumentPdf',
    Transaction: 'TransactionDocuments/GenerateTransactionDocumentPdf',
};

/** Business source label (PendingStatusResponse.Source) → ReferenceDocumentType name. */
const FINISH_SOURCE_DOCUMENT_TYPES = {
    Invoice: 'TaxInvoice',
    InvoiceReceipt: 'ReceiptTax',
    CreditInvoice: 'CreditNote',
};

/**
 * Series a stored snapshot belongs to. The snapshot's own shape wins over the status fields
 * because it is the DTO the owning endpoint serialized, so it identifies the counter even when
 * an older audit row carries no document type.
 */
const resolveFinishDocumentType = (pendingStatus, body) => {
    if (body && typeof body === 'object') {
        if (body.TransactionDocumentType || body.transactionDocumentType) return 'Transaction';
        const isInvoiceReceipt =
            body.IsInvoiceReceipt !== undefined ? body.IsInvoiceReceipt : body.isInvoiceReceipt;
        if (typeof isInvoiceReceipt === 'boolean') {
            return isInvoiceReceipt ? 'ReceiptTax' : 'Receipt';
        }
    }
    const documentType = pendingStatus?.documentType || pendingStatus?.DocumentType;
    if (documentType && FINISH_DOCUMENT_ENDPOINTS[documentType]) return documentType;

    const source = pendingStatus?.source || pendingStatus?.Source;
    return FINISH_SOURCE_DOCUMENT_TYPES[source] || documentType || 'TaxInvoice';
};

/** Throws for an unknown series rather than defaulting, which would burn a number elsewhere. */
const resolveFinishDocumentEndpoint = (documentType) => {
    const endpoint = FINISH_DOCUMENT_ENDPOINTS[documentType];
    if (!endpoint) throw new Error(`UnsupportedFinishDocumentType:${documentType}`);
    return endpoint;
};

/**
 * After a problem-invoice decision is accepted: create the PDF + DB row through the endpoint
 * that owns the reference's series. Idempotent when the decision is already on file.
 * @returns {{ type: 'pdf', blob: Blob } | { type: 'json', data: object } | { type: 'text', message: string }}
 */
const generateDocumentWithDecision = async (pendingStatus, decisionFlag, auth) => {
    const invoiceId = pendingStatus?.invoiceId || pendingStatus?.InvoiceId;
    const payload = extractInvoicePayloadFromStatus(pendingStatus);
    if (!payload) {
        throw new Error(
            'MissingStoredInvoiceSnapshot' // caller may translate
        );
    }
    const body = buildGenerateBodyFromSnapshot(payload, invoiceId, decisionFlag);
    if (!body) {
        throw new Error('MissingStoredInvoiceSnapshot');
    }

    const documentType = resolveFinishDocumentType(pendingStatus, body);
    const endpoint = resolveFinishDocumentEndpoint(documentType);

    // The Tax Authority already holds the decision for this reference. Issuing the PDF must
    // not request a confirmation number (that path would hit 462) — the generate endpoint
    // applies the sent decision's banners and saves the document on this same number.
    const awaitingDocument =
        (pendingStatus?.status || pendingStatus?.Status) === 'AwaitingDocument';
    if (awaitingDocument) {
        body.noNeedforConfirmationNumber = true;
        body.NoNeedforConfirmationNumber = true;
    }

    // Tells the server this is a resume of a held number: use it verbatim in this series and
    // refuse rather than allocate a new one if it is not the held reference.
    body.FinishPendingReference = true;
    body.finishPendingReference = true;

    const baseUrl = config[selectedEnvironment].API_URL;
    const response = await fetch(`${baseUrl}/${endpoint}`, {
        method: 'POST',
        headers: {
            Authorization: 'Bearer ' + auth,
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
    });

    if (!response.ok) {
        let errorMessage = 'Failed to generate document after decision';
        try {
            const errorText = await response.text();
            if (errorText && errorText.trim()) {
                errorMessage = errorText.trim().replace(/^"(.*)"$/, '$1');
            }
        } catch {
            /* ignore */
        }
        throw new Error(errorMessage);
    }

    const contentType = response.headers.get('content-type') || '';
    if (contentType.includes('application/json')) {
        const data = await response.json();
        return { type: 'json', data };
    }
    if (contentType.includes('text/plain')) {
        return { type: 'text', message: await response.text() };
    }
    const blob = await response.blob();
    return { type: 'pdf', blob };
};

const TaxAuthorityService = {
    requestTokenClient,
    checkAuthCompletionClient,
    requestTokenAccountant,
    checkAuthCompletionAccountant,
    getTokenDaysRemaining,
    deleteClientToken,
    processInvoiceDecision,
    checkInvoiceDecisionHealth,
    checkInvoiceDecisionAlive,
    getAccountantTokenDaysRemaining,
    deleteAccountantToken,
    getPendingInvoiceDecisionStatus,
    resolvePendingInvoiceDecision,
    getProblemInvoiceDecisions,
    compareSupplierInvoices,
    importSupplierInvoices,
    mapOptionToResolveAction,
    mapOptionToDecisionFlag,
    extractInvoicePayloadFromStatus,
    mergePendingStatusWithHeldRow,
    buildGenerateBodyFromSnapshot,
    resolveFinishDocumentType,
    resolveFinishDocumentEndpoint,
    generateDocumentWithDecision,
};

export default TaxAuthorityService;
